import createClient from 'openapi-fetch'
import type { Client, Middleware } from 'openapi-fetch'

import { ApiError, isRetryable, offlineError } from './errors'
import type { ApiFailure } from './errors'
import type { paths } from './generated'

const INIT_DATA_HEADER = 'X-Telegram-Init-Data'

const RETRY_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 300

export interface ApiSettings {
  baseUrl: string
  initData: () => string | undefined
  language: () => string
  onUnauthorized?: () => void
}

export type Api = Client<paths>

export function createApi(settings: ApiSettings): Api {
  const client = createClient<paths>({
    baseUrl: settings.baseUrl,
    fetch: (request) => retrying(request),
  })

  client.use(identify(settings))
  client.use(raise(settings))

  return client
}

function identify(settings: ApiSettings): Middleware {
  return {
    onRequest({ request }) {
      const initData = settings.initData()

      if (initData) {
        request.headers.set(INIT_DATA_HEADER, initData)
      }

      request.headers.set('Accept-Language', settings.language())

      return request
    },
  }
}

function raise(settings: ApiSettings): Middleware {
  return {
    async onResponse({ response }) {
      if (response.ok) {
        if (!carriesJson(response)) {
          throw new ApiError(response.status, {
            code: 'upstream_failed',
            message: `Expected JSON, the server answered ${typeOf(response)}`,
          })
        }

        return response
      }

      const body = await bodyOf(response)

      if (response.status === 401) {
        settings.onUnauthorized?.()
      }

      throw new ApiError(response.status, failureOf(body, response), body)
    },
  }
}

const EMPTY_STATUSES = [204, 205]

function carriesJson(response: Response): boolean {
  return EMPTY_STATUSES.includes(response.status) || typeOf(response).includes('json')
}

function typeOf(response: Response): string {
  const header = response.headers.get('Content-Type')

  if (!header) {
    return 'no content type'
  }

  return (header.split(';')[0] ?? header).trim().toLowerCase()
}

async function bodyOf(response: Response): Promise<unknown> {
  try {
    return await response.clone().json()
  } catch {
    // A gateway that answers HTML is still an error, just an unhelpful one.
    return undefined
  }
}

function failureOf(body: unknown, response: Response): ApiFailure {
  if (body && typeof body === 'object' && 'code' in body) {
    return body as ApiFailure
  }

  return { code: 'unknown', message: response.statusText || String(response.status) }
}

async function retrying(request: Request): Promise<Response> {
  let last: unknown

  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await fetch(attempt === 0 ? request : request.clone())
    } catch (cause) {
      if (request.signal.aborted) {
        throw cause
      }

      last = offlineError(cause)

      if (!isRetryable(last) || attempt === RETRY_ATTEMPTS - 1) {
        throw last
      }

      await sleep(RETRY_BASE_DELAY_MS * 2 ** attempt, request.signal)
    }
  }

  throw last
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, ms)

    function abort() {
      clearTimeout(timer)
      reject(signal.reason as Error)
    }

    signal.addEventListener('abort', abort, { once: true })
  })
}
